/**
 * Wandering trader offers (vanilla Java WanderingTraderTrades), DOM-free.
 *
 * Six offers per trader: five distinct picks from the "common" pool and one
 * from the "rare" pool, every one emeralds -> item, a few uses each, no restock
 * and no levelling (the trader never gains XP-based levels). Entries whose item
 * or block this build lacks (dyes, sea pickle, small dripleaf, extra saplings,
 * fish buckets, ...) are left out of the pools, so the common pool is thinner
 * than vanilla's. Costs and use counts are vanilla's where known.
 *
 * Also the spawn roll: every 24000 ticks a 2.5% chance (+2.5% per failure, cap
 * 7.5%, reset on success) that a trader turns up near the player.
 */

import { BLOCKS } from '../../blocks';
import { I } from '../../game/itemIds';
import type { Offer } from '../../game/trading';

/** [item or block name, emerald cost, count, max uses]. */
type Row = readonly [string, number, number, number];

const blockId = (name: string): number => BLOCKS.find((b) => b?.name === name)?.id ?? 0;
const itemId = (name: string): number => (I as Record<string, number>)[name] ?? 0;
/** Block names first (their ids are small), then item names. */
const idOf = (name: string): number => (/^[A-Z_]+$/.test(name) ? itemId(name) : blockId(name));

export const COMMON_ROWS: readonly Row[] = [
  ['SLIME_BALL', 4, 1, 5], ['Glowstone', 2, 1, 5], ['NAUTILUS_SHELL', 5, 1, 5],
  ['Fern', 1, 1, 12], ['Sugar Cane', 1, 1, 8], ['Pumpkin', 1, 1, 4], ['Kelp', 3, 1, 12], ['Cactus', 1, 1, 8],
  ['Dandelion', 1, 1, 12], ['Poppy', 1, 1, 12], ['Blue Orchid', 1, 1, 8], ['Allium', 1, 1, 12], ['Azure Bluet', 1, 1, 12],
  ['Red Tulip', 1, 1, 12], ['Orange Tulip', 1, 1, 12], ['White Tulip', 1, 1, 12], ['Pink Tulip', 1, 1, 12],
  ['Oxeye Daisy', 1, 1, 12], ['Cornflower', 1, 1, 12], ['Lily of the Valley', 1, 1, 7],
  ['WHEAT_SEEDS', 1, 1, 12], ['BEETROOT_SEEDS', 1, 1, 12], ['PUMPKIN_SEEDS', 1, 1, 12], ['MELON_SEEDS', 1, 1, 12],
  ['Oak Sapling', 5, 1, 8],
  ['Brown Mushroom', 1, 1, 12], ['Red Mushroom', 1, 1, 12],
  ['Lily Pad', 1, 2, 5], ['Pointed Dripstone', 1, 2, 5], ['Rooted Dirt', 1, 2, 5], ['Moss Block', 1, 2, 5],
  ['Sand', 1, 8, 8], ['Red Sand', 1, 4, 6],
  ['Tube Coral Block', 3, 1, 8], ['Brain Coral Block', 3, 1, 8], ['Bubble Coral Block', 3, 1, 8], ['Fire Coral Block', 3, 1, 8], ['Horn Coral Block', 3, 1, 8],
];

export const RARE_ROWS: readonly Row[] = [
  ['Packed Ice', 3, 1, 6], ['Blue Ice', 6, 1, 6], ['GUNPOWDER', 1, 1, 8], ['Podzol', 3, 3, 6],
  ['Oak Log', 1, 8, 4], ['Spruce Log', 1, 8, 4], ['Birch Log', 1, 8, 4], ['Jungle Log', 1, 8, 4],
  ['Acacia Log', 1, 8, 4], ['Dark Oak Log', 1, 8, 4], ['Cherry Log', 1, 8, 4], ['Mangrove Log', 1, 8, 4],
];

const resolve = (rows: readonly Row[]): { id: number; row: Row }[] =>
  rows.map((row) => ({ id: idOf(row[0]), row })).filter((r) => r.id > 0);

/** The pools after dropping everything this build lacks. */
export const COMMON_POOL = resolve(COMMON_ROWS);
export const RARE_POOL = resolve(RARE_ROWS);

export const TRADER_COMMON_OFFERS = 5;
export const TRADER_RARE_OFFERS = 1;

const toOffer = (e: { id: number; row: Row }): Offer => ({
  a: { id: I.EMERALD, count: e.row[1], dmg: 0 }, b: null, out: { id: e.id, count: e.row[2], dmg: 0 },
  uses: 0, maxUses: e.row[3], xp: 1, mult: 0.05, demand: 0,
});

/** A fresh trader's offers: 5 distinct common picks, then 1 rare. */
export function rollTraderOffers(rnd: () => number): Offer[] {
  const out: Offer[] = [];
  const pool = COMMON_POOL.slice();
  for (let i = 0; i < TRADER_COMMON_OFFERS && pool.length; i++) out.push(toOffer(pool.splice(Math.floor(rnd() * pool.length), 1)[0]));
  for (let i = 0; i < TRADER_RARE_OFFERS && RARE_POOL.length; i++) out.push(toOffer(RARE_POOL[Math.floor(rnd() * RARE_POOL.length)]));
  return out;
}

// ------------------------------------------------------------- spawn roll

/** Spawn chance in permille: 2.5%, 5%, 7.5%. */
export const SPAWN_CHANCE_START = 25;
export const SPAWN_CHANCE_STEP = 25;
export const SPAWN_CHANCE_MAX = 75;
/** Ticks between rolls (one game day). */
export const SPAWN_INTERVAL = 24000;

export class TraderSpawnRoll {
  /** Ticks until the next roll. */
  timer = SPAWN_INTERVAL;
  /** Current chance in permille. */
  chance = SPAWN_CHANCE_START;

  /**
   * One game tick. On a roll day returns whether the dice came up; the caller
   * then tries to place a trader and reports back with `result(ok)`.
   */
  tick(rnd: () => number): boolean {
    if (--this.timer > 0) return false;
    this.timer = SPAWN_INTERVAL;
    const hit = rnd() * 1000 < this.chance;
    if (!hit) this.chance = Math.min(SPAWN_CHANCE_MAX, this.chance + SPAWN_CHANCE_STEP);
    return hit;
  }

  /** The caller's placement outcome after a hit: success resets the ramp, a failed placement keeps ramping. */
  result(ok: boolean): void {
    if (ok) this.chance = SPAWN_CHANCE_START;
    else this.chance = Math.min(SPAWN_CHANCE_MAX, this.chance + SPAWN_CHANCE_STEP);
  }

  save(): [number, number] { return [this.timer, this.chance]; }
  load(d: unknown): void {
    if (!Array.isArray(d)) return;
    const t = Number(d[0]), c = Number(d[1]);
    if (Number.isFinite(t) && t > 0 && t <= SPAWN_INTERVAL) this.timer = Math.floor(t);
    if (Number.isFinite(c) && c >= SPAWN_CHANCE_START && c <= SPAWN_CHANCE_MAX) this.chance = Math.floor(c);
  }
}
